/**
 * Terminology Glossary
 *
 * Manages consistent healthcare terminology translations.
 * Implements RFP requirements for:
 * - Terminology consistency across translations
 * - Healthcare-specific vocabulary
 * - Role-based terminology variants
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import { useTabbedChrome } from '@/components/weavein';

// ============================================================================
// TYPES
// ============================================================================

export type TermCategory =
  | 'medical'
  | 'administrative'
  | 'program'
  | 'sdoh'
  | 'communication'
  | 'navigation';

export type TermTone = 'formal' | 'friendly' | 'technical';

export interface GlossaryTerm {
  id: string;
  english: string;
  spanish: string;
  spanishFormal?: string; // Usted form
  spanishFriendly?: string; // Tu form
  category: TermCategory;
  tone: TermTone;
  context?: string;
  contextEs?: string;
  abbreviation?: string;
  abbreviationEs?: string;
  doNotTranslate?: boolean;
  notes?: string;
  examples?: {
    en: string;
    es: string;
  }[];
  relatedTerms?: string[];
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// PREDEFINED GLOSSARY
// ============================================================================

const HEALTHCARE_GLOSSARY: GlossaryTerm[] = [
  // Medical Terms
  {
    id: '1',
    english: 'Care Plan',
    spanish: 'Plan de Cuidado',
    spanishFormal: 'Plan de Atención',
    category: 'medical',
    tone: 'friendly',
    context: 'A document outlining the member\'s health goals and actions',
    contextEs: 'Un documento que describe las metas de salud y acciones del miembro',
    examples: [
      { en: 'Your care plan includes goals for managing diabetes.', es: 'Su plan de cuidado incluye metas para manejar la diabetes.' },
    ],
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '2',
    english: 'Enrollment',
    spanish: 'Inscripción',
    spanishFormal: 'Inscripción',
    category: 'administrative',
    tone: 'friendly',
    context: 'The process of signing up for a program',
    contextEs: 'El proceso de registrarse en un programa',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '3',
    english: 'Member',
    spanish: 'Miembro',
    spanishFormal: 'Participante',
    category: 'administrative',
    tone: 'friendly',
    context: 'A person enrolled in a health program',
    contextEs: 'Una persona inscrita en un programa de salud',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '4',
    english: 'Promotora',
    spanish: 'Promotora',
    category: 'program',
    tone: 'friendly',
    doNotTranslate: true,
    context: 'A community health worker who provides outreach and support',
    contextEs: 'Una trabajadora de salud comunitaria que brinda apoyo',
    notes: 'Do not translate - use "Promotora" in both languages',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '5',
    english: 'Follow-up',
    spanish: 'Seguimiento',
    spanishFormal: 'Seguimiento',
    category: 'communication',
    tone: 'friendly',
    context: 'A subsequent contact to check on progress',
    contextEs: 'Un contacto posterior para verificar el progreso',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '6',
    english: 'Appointment',
    spanish: 'Cita',
    spanishFormal: 'Cita',
    category: 'medical',
    tone: 'friendly',
    context: 'A scheduled meeting with a healthcare provider',
    contextEs: 'Una reunión programada con un proveedor de salud',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  // SDOH Terms
  {
    id: '7',
    english: 'Social Determinants of Health',
    spanish: 'Determinantes Sociales de la Salud',
    abbreviation: 'SDOH',
    abbreviationEs: 'DSS',
    category: 'sdoh',
    tone: 'technical',
    context: 'Factors in a person\'s environment that affect health outcomes',
    contextEs: 'Factores en el entorno de una persona que afectan los resultados de salud',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '8',
    english: 'Housing Instability',
    spanish: 'Inestabilidad de Vivienda',
    spanishFriendly: 'Problemas de vivienda',
    category: 'sdoh',
    tone: 'formal',
    context: 'Difficulty maintaining stable housing',
    contextEs: 'Dificultad para mantener una vivienda estable',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '9',
    english: 'Food Insecurity',
    spanish: 'Inseguridad Alimentaria',
    spanishFriendly: 'Dificultad para conseguir comida',
    category: 'sdoh',
    tone: 'formal',
    context: 'Lack of reliable access to sufficient food',
    contextEs: 'Falta de acceso confiable a suficiente comida',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '10',
    english: 'Transportation Barrier',
    spanish: 'Barrera de Transporte',
    spanishFriendly: 'Dificultad para transportarse',
    category: 'sdoh',
    tone: 'formal',
    context: 'Difficulty accessing transportation to appointments',
    contextEs: 'Dificultad para acceder a transporte para citas',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  // Program Terms
  {
    id: '11',
    english: 'CalAIM',
    spanish: 'CalAIM',
    category: 'program',
    tone: 'technical',
    doNotTranslate: true,
    context: 'California Advancing and Innovating Medi-Cal',
    contextEs: 'California Advancing and Innovating Medi-Cal',
    notes: 'Program name - do not translate',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '12',
    english: 'Enhanced Care Management',
    spanish: 'Gestión de Cuidado Mejorada',
    abbreviation: 'ECM',
    abbreviationEs: 'GCM',
    category: 'program',
    tone: 'technical',
    context: 'A CalAIM program providing intensive care coordination',
    contextEs: 'Un programa CalAIM que brinda coordinación de cuidado intensivo',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '13',
    english: 'Community Supports',
    spanish: 'Apoyos Comunitarios',
    category: 'program',
    tone: 'friendly',
    context: 'Services to address social needs',
    contextEs: 'Servicios para abordar necesidades sociales',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  // Communication Terms
  {
    id: '14',
    english: 'Outreach',
    spanish: 'Alcance',
    spanishFormal: 'Contacto inicial',
    category: 'communication',
    tone: 'friendly',
    context: 'Initial contact with potential members',
    contextEs: 'Contacto inicial con miembros potenciales',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '15',
    english: 'Reminder',
    spanish: 'Recordatorio',
    category: 'communication',
    tone: 'friendly',
    context: 'A message to remind about an upcoming event',
    contextEs: 'Un mensaje para recordar sobre un evento próximo',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  // Navigation Terms
  {
    id: '16',
    english: 'Benefits',
    spanish: 'Beneficios',
    category: 'navigation',
    tone: 'friendly',
    context: 'Services or support a member is entitled to',
    contextEs: 'Servicios o apoyo a los que un miembro tiene derecho',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '17',
    english: 'Referral',
    spanish: 'Referencia',
    spanishFormal: 'Derivación',
    category: 'navigation',
    tone: 'friendly',
    context: 'Connecting a member to another service',
    contextEs: 'Conectar a un miembro con otro servicio',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: '18',
    english: 'Consent',
    spanish: 'Consentimiento',
    category: 'administrative',
    tone: 'formal',
    context: 'Permission given by a member',
    contextEs: 'Permiso otorgado por un miembro',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
];

const CATEGORY_CONFIG: Record<TermCategory, { label: string; labelEs: string; color: string }> = {
  medical: { label: 'Medical', labelEs: 'Médico', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
  administrative: { label: 'Administrative', labelEs: 'Administrativo', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  program: { label: 'Program', labelEs: 'Programa', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  sdoh: { label: 'SDOH', labelEs: 'DSS', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
  communication: { label: 'Communication', labelEs: 'Comunicación', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' },
  navigation: { label: 'Navigation', labelEs: 'Navegación', color: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200' },
};

// ============================================================================
// TERM EDITOR DIALOG
// ============================================================================

interface TermEditorProps {
  term: GlossaryTerm | null;
  onSave: (term: GlossaryTerm) => void;
  onCancel: () => void;
  isNew?: boolean;
}

function TermEditor({ term, onSave, onCancel, isNew = false }: TermEditorProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language || 'en';

  const [english, setEnglish] = useState(term?.english ?? '');
  const [spanish, setSpanish] = useState(term?.spanish ?? '');
  const [spanishFormal, setSpanishFormal] = useState(term?.spanishFormal ?? '');
  const [spanishFriendly, setSpanishFriendly] = useState(term?.spanishFriendly ?? '');
  const [category, setCategory] = useState<TermCategory>(term?.category ?? 'medical');
  const [tone, setTone] = useState<TermTone>(term?.tone ?? 'friendly');
  const [context, setContext] = useState(term?.context ?? '');
  const [contextEs, setContextEs] = useState(term?.contextEs ?? '');
  const [abbreviation, setAbbreviation] = useState(term?.abbreviation ?? '');
  const [abbreviationEs, setAbbreviationEs] = useState(term?.abbreviationEs ?? '');
  const [doNotTranslate, setDoNotTranslate] = useState(term?.doNotTranslate ?? false);
  const [notes, setNotes] = useState(term?.notes ?? '');

  const handleSave = () => {
    const now = new Date().toISOString();
    const newTerm: GlossaryTerm = {
      id: term?.id ?? `term_${Date.now()}`,
      english,
      spanish: doNotTranslate ? english : spanish,
      spanishFormal: spanishFormal || undefined,
      spanishFriendly: spanishFriendly || undefined,
      category,
      tone,
      context: context || undefined,
      contextEs: contextEs || undefined,
      abbreviation: abbreviation || undefined,
      abbreviationEs: abbreviationEs || undefined,
      doNotTranslate,
      notes: notes || undefined,
      createdAt: term?.createdAt ?? now,
      updatedAt: now,
    };
    onSave(newTerm);
  };

  return (
    <Dialog open={true} onOpenChange={() => onCancel()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? t('glossary.addTerm', 'Add Term')
              : t('glossary.editTerm', 'Edit Term')}
          </DialogTitle>
          <DialogDescription>
            {t('glossary.editorDescription', 'Define consistent translations for healthcare terminology')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="english">{t('glossary.english', 'English')}</Label>
              <Input
                id="english"
                value={english}
                onChange={(e) => setEnglish(e.target.value)}
                placeholder="Enter English term"
              />
            </div>
            <div>
              <Label htmlFor="spanish">{t('glossary.spanish', 'Spanish')}</Label>
              <Input
                id="spanish"
                value={spanish}
                onChange={(e) => setSpanish(e.target.value)}
                placeholder="Enter Spanish translation"
                disabled={doNotTranslate}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="doNotTranslate"
              checked={doNotTranslate}
              onChange={(e) => setDoNotTranslate(e.target.checked)}
              className="rounded"
            />
            <Label htmlFor="doNotTranslate" className="text-sm">
              {t('glossary.doNotTranslate', 'Do not translate (use English in both languages)')}
            </Label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="spanishFormal">{t('glossary.spanishFormal', 'Spanish (Formal/Usted)')}</Label>
              <Input
                id="spanishFormal"
                value={spanishFormal}
                onChange={(e) => setSpanishFormal(e.target.value)}
                placeholder="Optional formal variant"
                disabled={doNotTranslate}
              />
            </div>
            <div>
              <Label htmlFor="spanishFriendly">{t('glossary.spanishFriendly', 'Spanish (Friendly/Tú)')}</Label>
              <Input
                id="spanishFriendly"
                value={spanishFriendly}
                onChange={(e) => setSpanishFriendly(e.target.value)}
                placeholder="Optional friendly variant"
                disabled={doNotTranslate}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="abbreviation">{t('glossary.abbreviation', 'Abbreviation (EN)')}</Label>
              <Input
                id="abbreviation"
                value={abbreviation}
                onChange={(e) => setAbbreviation(e.target.value)}
                placeholder="e.g., ECM"
              />
            </div>
            <div>
              <Label htmlFor="abbreviationEs">{t('glossary.abbreviationEs', 'Abbreviation (ES)')}</Label>
              <Input
                id="abbreviationEs"
                value={abbreviationEs}
                onChange={(e) => setAbbreviationEs(e.target.value)}
                placeholder="e.g., GCM"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="category">{t('glossary.category', 'Category')}</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as TermCategory)}>
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {locale === 'es' ? config.labelEs : config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="tone">{t('glossary.tone', 'Default Tone')}</Label>
              <Select value={tone} onValueChange={(v) => setTone(v as TermTone)}>
                <SelectTrigger id="tone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="formal">{t('glossary.tone.formal', 'Formal')}</SelectItem>
                  <SelectItem value="friendly">{t('glossary.tone.friendly', 'Friendly')}</SelectItem>
                  <SelectItem value="technical">{t('glossary.tone.technical', 'Technical')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="context">{t('glossary.context', 'Context (EN)')}</Label>
              <Textarea
                id="context"
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="When to use this term"
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="contextEs">{t('glossary.contextEs', 'Context (ES)')}</Label>
              <Textarea
                id="contextEs"
                value={contextEs}
                onChange={(e) => setContextEs(e.target.value)}
                placeholder="Cuándo usar este término"
                rows={2}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="notes">{t('glossary.notes', 'Notes')}</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes for translators"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!english || (!doNotTranslate && !spanish)}>
            {t('common.save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TerminologyGlossary() {
  const tabbed = useTabbedChrome();
  const { t, i18n } = useTranslation();
  const locale = i18n?.language || 'en';

  const [terms, setTerms] = useState<GlossaryTerm[]>(HEALTHCARE_GLOSSARY);
  const [editingTerm, setEditingTerm] = useState<GlossaryTerm | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<TermCategory | 'all'>('all');
  const [activeTab, setActiveTab] = useState<'list' | 'quick'>('list');

  const filteredTerms = useMemo(() => {
    return terms.filter((term) => {
      const matchesSearch =
        term.english.toLowerCase().includes(searchQuery.toLowerCase()) ||
        term.spanish.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = filterCategory === 'all' || term.category === filterCategory;
      return matchesSearch && matchesCategory;
    });
  }, [terms, searchQuery, filterCategory]);

  const handleSaveTerm = (term: GlossaryTerm) => {
    setTerms((prev) => {
      const existing = prev.findIndex((t) => t.id === term.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = term;
        return updated;
      }
      return [...prev, term];
    });
    setEditingTerm(null);
    setIsCreating(false);
  };

  const handleDeleteTerm = (termId: string) => {
    setTerms((prev) => prev.filter((t) => t.id !== termId));
  };

  const getTranslation = (term: GlossaryTerm, toneOverride?: TermTone): string => {
    const effectiveTone = toneOverride ?? term.tone;
    if (term.doNotTranslate) return term.english;
    if (effectiveTone === 'formal' && term.spanishFormal) return term.spanishFormal;
    if (effectiveTone === 'friendly' && term.spanishFriendly) return term.spanishFriendly;
    return term.spanish;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && (
          <h1 className="text-2xl font-bold">
            {t('glossary.title', 'Terminology Glossary')}
          </h1>
          )}
          <p className="text-muted-foreground">
            {t('glossary.subtitle', 'Consistent healthcare terminology translations')}
          </p>
        </div>
        <Button onClick={() => setIsCreating(true)}>
          + {t('glossary.addTerm', 'Add Term')}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'list' | 'quick')}>
        <TabsList>
          <TabsTrigger value="list">{t('glossary.fullList', 'Full List')}</TabsTrigger>
          <TabsTrigger value="quick">{t('glossary.quickReference', 'Quick Reference')}</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-4">
                <div className="flex-1">
                  <Input
                    placeholder={t('glossary.search', 'Search terms...')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Select
                  value={filterCategory}
                  onValueChange={(v) => setFilterCategory(v as TermCategory | 'all')}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder={t('glossary.filterCategory', 'Category')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('glossary.allCategories', 'All Categories')}</SelectItem>
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {locale === 'es' ? config.labelEs : config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Terms Table */}
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('glossary.english', 'English')}</TableHead>
                    <TableHead>{t('glossary.spanish', 'Spanish')}</TableHead>
                    <TableHead>{t('glossary.category', 'Category')}</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTerms.map((term) => {
                    const categoryConfig = CATEGORY_CONFIG[term.category];
                    return (
                      <TableRow key={term.id}>
                        <TableCell>
                          <div className="font-medium">{term.english}</div>
                          {term.abbreviation && (
                            <div className="text-xs text-muted-foreground">
                              ({term.abbreviation})
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>{term.doNotTranslate ? <em className="text-muted-foreground">{term.spanish}</em> : term.spanish}</div>
                          {term.abbreviationEs && (
                            <div className="text-xs text-muted-foreground">
                              ({term.abbreviationEs})
                            </div>
                          )}
                          {term.doNotTranslate && (
                            <Badge variant="outline" className="text-xs mt-1">
                              {t('glossary.noTranslation', 'No translation')}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={categoryConfig.color}>
                            {locale === 'es' ? categoryConfig.labelEs : categoryConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingTerm(term)}
                            >
                              {t('common.edit', 'Edit')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTerm(term.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              ×
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quick">
          {/* Quick Reference Cards by Category */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(CATEGORY_CONFIG).map(([category, config]) => {
              const categoryTerms = terms.filter((t) => t.category === category);
              if (categoryTerms.length === 0) return null;

              return (
                <Card key={category}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Badge className={config.color}>
                        {locale === 'es' ? config.labelEs : config.label}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {categoryTerms.map((term) => (
                        <div key={term.id} className="text-sm">
                          <span className="font-medium">{term.english}</span>
                          <span className="text-muted-foreground mx-1">→</span>
                          <span>{getTranslation(term)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Editor Dialog */}
      {(editingTerm || isCreating) && (
        <TermEditor
          term={editingTerm}
          onSave={handleSaveTerm}
          onCancel={() => {
            setEditingTerm(null);
            setIsCreating(false);
          }}
          isNew={isCreating}
        />
      )}
    </div>
  );
}

export default TerminologyGlossary;
